<template>
  <vs-wrapper>
    <v-row>
      <v-col cols="auto" class="pr-12">
        <vs-text type="overline">{{ stopsEnabled ? 'Delivered' : 'Requested' }}</vs-text>
        <div class="totals-item pt-2" data-test="total-inventory-row">
          <job-request-trip-total-popover
            title="Inventory"
            :click-text="`${totalInv}`"
            :rows="inventoryQuantityByType"
          />
        </div>
      </v-col>
      <v-col>
        <div class="totals-item">
          <vs-text type="overline">Distance</vs-text>
        </div>
        <div class="totals-item pt-2" data-test="total-distance-row">
          <job-request-trip-total-popover
            title="Traveled"
            :click-text="formattedTotalTripDistance"
            :rows="tripDistancePerJob"
          />
        </div>
      </v-col>
      <v-col>
        <div class="totals-item">
          <vs-text type="overline">Duration</vs-text>
        </div>
        <div class="totals-item pt-2" data-test="total-duration-row">
          <job-request-trip-total-popover
            title="Time"
            :click-text="formattedTotalDuration"
            :rows="durationPerJob"
          />
        </div>
      </v-col>
    </v-row>
  </vs-wrapper>
</template>

<script setup lang="ts">
import VsText from '@/components/vision/VsText.vue'
import { computed } from 'vue'
import { formatDuration } from '@/utils/date-formatting'
import moment from 'moment/moment'
import { TripMapDistanceUnits } from '@/store/modules/organization-config'
import { kmToMilesConversion } from '@/utils/constants'
import { useStore } from 'vuex-composition-helpers'
import { JobRequestJobsResponse } from '@/models/types/job-request-jobs-response'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import JobRequestTripTotalPopover from '@/components/request/JobRequestTripTotalPopover.vue'
import { sortBy } from 'lodash'
import { JobStopType } from '@ironsight/types'
import { useGetJobRequestedStopsQuery, useGetJobStopsQuery } from '@/api/jobs'

const props = defineProps<{
  jobs: JobRequestJobsResponse[]
  stopsEnabled: boolean
}>()

const store = useStore()

const actualStopQueries = computed(() =>
  props.stopsEnabled ? props.jobs.map((job) => useGetJobStopsQuery(job.id)) : [],
)
const actualStops = computed(() => {
  return actualStopQueries.value
    .filter((queryData) => !queryData.isLoading.value && !queryData.error.value)
    .flatMap((queryData) => queryData.data.value ?? [])
})
const requestedStopQueries = computed(() =>
  props.stopsEnabled ? [] : props.jobs.map((job) => useGetJobRequestedStopsQuery(job.id)),
)
const requestedStops = computed(() => {
  return requestedStopQueries.value
    .filter((queryData) => !queryData.isLoading.value && !queryData.error.value)
    .flatMap((queryData) => queryData.data.value ?? [])
})
const dropoffReceipts = computed(() => {
  return actualStops.value
    .filter((stop) => stop.stopType === JobStopType.DropOff && stop.locationId !== null)
    .flatMap((stop) => stop.inventoryReceipts)
})
const requestedPickupInventory = computed(() => {
  return requestedStops.value
    .filter((stop) => stop.stopType === JobStopType.PickUp)
    .flatMap((stop) => stop.inventory)
})

const inventoryRows = computed(() => {
  if (props.stopsEnabled) {
    return dropoffReceipts.value.map((receipt) => ({
      inventoryName: receipt.inventorySubTypeName,
      quantity: receipt.quantity,
    }))
  }

  return requestedPickupInventory.value.map((inventory) => ({
    inventoryName: inventory.inventorySubTypeName,
    quantity: inventory.quantity,
  }))
})

const totalInv = computed(() =>
  inventoryRows.value.reduce((accumulatedQuantity, row) => accumulatedQuantity + row.quantity, 0),
)

const formattedTotalDuration = computed(() => {
  const totalDuration = props.jobs.reduce(
    (acc, job) =>
      job.active_duration ? acc.add(moment.duration(job.active_duration)) : moment.duration(),
    moment.duration(),
  )

  return formatDuration(totalDuration, 2, true)
})

const tripMapDistanceUnit = computed<TripMapDistanceUnits>(
  () => store.state.organizationConfig.tripMapDistanceUnit,
)

const formattedTotalTripDistance = computed<string>(() => {
  const totalDistance = props.jobs.reduce((a, job) => a + job.total_distance, 0)
  return formatTripDistance(totalDistance)
})

function formatTripDistance(distanceInMeters: number) {
  const totalDistanceKm = distanceInMeters / 1000
  const conversionFactor =
    tripMapDistanceUnit.value === TripMapDistanceUnits.MILES ? kmToMilesConversion : 1
  const unitLabel = tripMapDistanceUnit.value === TripMapDistanceUnits.MILES ? 'mi' : 'km'

  return `${(totalDistanceKm * conversionFactor).toFixed(1)} ${unitLabel}`
}

const tripDistancePerJob = computed(() =>
  props.jobs.map((job) => ({
    key: `#${job.number}`,
    value: formatTripDistance(job.total_distance),
  })),
)

const durationPerJob = computed(() =>
  props.jobs.map((job) => ({
    key: `#${job.number}`,
    value: formatDuration(moment.duration(job.active_duration), 2, true),
  })),
)

const inventoryQuantityByType = computed(() => {
  const quantityByInventoryName = inventoryRows.value.reduce((map, row) => {
    const key = row.inventoryName
    const existingQty = map.get(key) ?? 0
    map.set(key, existingQty + row.quantity)

    return map
  }, new Map<string, number>())

  return sortBy(
    [...quantityByInventoryName].map((row) => ({
      key: row[0],
      value: `${row[1]}`,
    })),
    (x) => x.key.toLowerCase(),
  )
})
</script>

<style scoped>
.totals-item {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
